'use client'

import {
  CognitiveFlexibilityTask as CognitiveFlexibilityTaskType,
  QuestionType
} from '@/api/services/types/questionTypes'
import { Card, CardContent } from '@/components/card'
import { Input } from '@/components/forms/inputs/text/Input'
import { Button } from '@/components/ui/common/Button'
import { useTestResponse } from '@/hooks/useTestResponse'
import { cn } from '@/utils/common'
import * as React from 'react'
interface CognitiveFlexibilityTaskProps {
  data?: CognitiveFlexibilityTaskType
  moduleId: string
  questionId: string
  onComplete?: (results: TaskResults) => void
  onBack?: () => void
  className?: string
  onStart?: () => void
}

interface TaskResults {
  list1: {
    correctAnswers: number
    totalAnswers: number
    timeSpent: number
  }
  list2: {
    correctAnswers: number
    totalAnswers: number
    timeSpent: number
  }
  list3: {
    correctAnswers: number
    totalAnswers: number
    timeSpent: number
  }
  totalCorrect: number
  totalQuestions: number
}

enum TaskStage {
  LIST_1 = 1, // dodawanie 7
  LIST_2 = 2, // odejmowanie 7
  LIST_3 = 3, // na przemian dodawanie i odejmowanie 7
}

export function CognitiveFlexibilityTask({
  data,
  moduleId,
  questionId,
  onComplete,
  className
}: CognitiveFlexibilityTaskProps) {
  const [stage, setStage] = React.useState<TaskStage>(TaskStage.LIST_1)
  const [userAnswers, setUserAnswers] = React.useState<string[]>([])
  const [remainingTime, setRemainingTime] = React.useState<number>(0)
  const [startTime, setStartTime] = React.useState<number>(0)
  const [results, setResults] = React.useState<TaskResults>({
    list1: { correctAnswers: 0, totalAnswers: 0, timeSpent: 0 },
    list2: { correctAnswers: 0, totalAnswers: 0, timeSpent: 0 },
    list3: { correctAnswers: 0, totalAnswers: 0, timeSpent: 0 },
    totalCorrect: 0,
    totalQuestions: 0
  })
  const { addQuestionResponse } = useTestResponse()

  React.useEffect(() => {
    startTask()
  }, [])

  const getCurrentList = () => {
    if (!data) return []
    if (stage === TaskStage.LIST_1)
      return data.operations.filter((op) => op.listType === 'ADDITION')
    if (stage === TaskStage.LIST_2)
      return data.operations.filter((op) => op.listType === 'SUBTRACTION')
    if (stage === TaskStage.LIST_3)
      return data.operations.filter((op) => op.listType === 'ALTERNATING')
    return []
  }

  const getInstruction = () => {
    if (stage === TaskStage.LIST_1) return 'Dodaj 7 do każdej liczby'
    if (stage === TaskStage.LIST_2) return 'Odejmij 7 od każdej liczby'
    if (stage === TaskStage.LIST_3)
      return 'Na przemian dodawaj i odejmuj 7 (zacznij od dodawania)'
    return ''
  }

  const getTimeLimit = () => {
    if (!data) return 30
    if (stage === TaskStage.LIST_1) return data.additionListTimeSeconds
    if (stage === TaskStage.LIST_2) return data.subtractionListTimeSeconds
    if (stage === TaskStage.LIST_3) return data.alternatingListTimeSeconds
    return 30
  }

  const calculateCorrectAnswers = (_: number, answers: string[]): number => {
    let correct = 0
    const currentList = getCurrentList()

    for (let i = 0; i < currentList.length; i++) {
      const expectedAnswer = currentList[i].correctResult
      const userAnswer = Number.parseInt(answers[i] || '')

      if (!isNaN(userAnswer) && userAnswer === expectedAnswer) {
        correct++
      }
    }

    return correct
  }

  const startList = React.useCallback(
    (listStage: TaskStage) => {

        setStage(listStage)
        const currentList = getCurrentList()
        setUserAnswers(Array(currentList.length).fill(''))
        setRemainingTime(getTimeLimit())
        setStartTime(Date.now())
    },
    [data]
  )

  const startTask = React.useCallback(() => {
    startList(TaskStage.LIST_1)
  }, [startList])

  const submitList = React.useCallback(() => {
    const timeSpent = (Date.now() - startTime) / 1000
    const listIndex = stage - 1
    const correctAnswers = calculateCorrectAnswers(listIndex, userAnswers)
    const answeredCount = userAnswers.filter((a) => a !== '').length

    setResults((prev) => {
      const newResults = { ...prev }

      if (listIndex === 0) {
        newResults.list1 = {
          correctAnswers,
          totalAnswers: answeredCount,
          timeSpent
        }
      } else if (listIndex === 1) {
        newResults.list2 = {
          correctAnswers,
          totalAnswers: answeredCount,
          timeSpent
        }
      } else {
        newResults.list3 = {
          correctAnswers,
          totalAnswers: answeredCount,
          timeSpent
        }
      }

      newResults.totalCorrect =
        newResults.list1.correctAnswers +
        newResults.list2.correctAnswers +
        newResults.list3.correctAnswers

      newResults.totalQuestions = data?.operations.length || 0

      return newResults
    })

    if (stage === TaskStage.LIST_1) {
      startList(TaskStage.LIST_2)
    } else if (stage === TaskStage.LIST_2) {
      startList(TaskStage.LIST_3)
    } else {

        // Zapisz odpowiedź w kontekście
        addQuestionResponse(moduleId, {
          questionId,
          questionType: QuestionType.COGNITIVE_FLEXIBILITY_TASK,
          cognitiveFlexibilityAnswer: {
            operations:
              data?.operations.map((op, index) => ({
                listType:
                  op.listType === 'ALTERNATING'
                    ? index % 2 === 0
                      ? 'ADDITION'
                      : 'SUBTRACTION'
                    : op.listType,
                listIndex: op.listIndex,
                operationType: op.operationType,
                userAnswer: Number(userAnswers[index]) || 0,
                responseTimeMillis: timeSpent * 1000
              })) || []
          }
        })
        setTimeout(() => {
        if (onComplete) {
          onComplete(results)
        }
      }, 500)
    }
  }, [
    stage,
    startTime,
    userAnswers,
    data,
    startList,
    onComplete,
    moduleId,
    questionId,
    addQuestionResponse,
    results
  ])

  const handleInputChange = (index: number, value: string) => {
    const newAnswers = [...userAnswers]
    newAnswers[index] = value.replace(/[^0-9-]/g, '')
    setUserAnswers(newAnswers)
  }

  React.useEffect(() => {
    if (
      stage !== TaskStage.LIST_1 &&
      stage !== TaskStage.LIST_2 &&
      stage !== TaskStage.LIST_3
    )
      return

    if (remainingTime <= 0) {
      submitList()
      return
    }

    const timer = setInterval(() => {
      setRemainingTime((prev) => prev - 1)
    }, 1000)

    return () => clearInterval(timer)
  }, [stage, remainingTime, submitList])

  const areAllInputsFilled = () => {
    return userAnswers.every((answer) => answer !== '')
  }

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }


  return (
    <div className={cn('flex flex-col', className)}>
      <main className="flex flex-1 flex-col items-center justify-center p-4">
       
        { (
          <div className="relative">
            {(stage === TaskStage.LIST_1 ||
              stage === TaskStage.LIST_2 ||
              stage === TaskStage.LIST_3) && (
              <div className="absolute -top-8 right-0 text-sm font-medium text-gray-600 bg-transparent px-3 py-1 rounded-md">
                Pozostały czas: {formatTime(remainingTime)}
              </div>
            )}
            <Card className="w-full max-w-3xl shadow-sm">
              <CardContent className="p-6">
                {(stage === TaskStage.LIST_1 ||
                  stage === TaskStage.LIST_2 ||
                  stage === TaskStage.LIST_3) && (
                  <div className="flex flex-col items-center gap-8 py-8">
                    <h1 className="text-center text-xl font-bold">
                      Lista {stage}: {getInstruction()}
                    </h1>

                    <div className="w-full max-w-md">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {getCurrentList().map((operation, index) => (
                          <div className="flex items-center justify-center gap-2 sm:gap-4" key={index}>
                            <div className="flex h-10 sm:h-12 w-12 sm:w-16 items-center justify-center rounded-lg border border-[#2bcaca]/20 bg-[#2bcaca]/10 text-lg sm:text-xl font-medium">
                              {operation.firstOperand}
                            </div>
                            <span className="text-base sm:text-lg">=</span>
                            <Input
                              className="w-16 sm:w-20 text-center text-base sm:text-lg"
                              inputMode="numeric"
                              onChange={(e) => handleInputChange(index, e.target.value)}
                              placeholder="?"
                              type="text"
                              value={userAnswers[index]}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button
                      className="mt-4 bg-[#2bcaca] hover:bg-[#2b9999] text-white"
                      disabled={!areAllInputsFilled()}
                      onClick={submitList}
                    >
                      {stage === TaskStage.LIST_3 ? 'Zakończ' : 'Następna lista'}
                    </Button>
                  </div>
                )}

              
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* <footer className="flex justify-end p-4">
        {onBack && (
          <Button
            className="bg-indigo-600 text-white hover:bg-indigo-700"
            disabled={stage !== TaskStage.RESULT}
            onClick={onBack}
          >
            Dalej
          </Button>
        )}
      </footer> */}
    </div>
  )
}
